import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import Modal from './Modal';
import {
    GREEN,
    PALE_WHITE_1,
    PALE_WHITE_3,
    PALE_WHITE_4,
    PALE_WHITE_6,
    SECONDARY_COLOR,
    WHITE
} from '../../appearance/Colors';

const modalStyle = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(92vw, 420px)',
        borderRadius: 20,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        background: '#151515',
        padding: 0,
        overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0, 0, 0, 0.48)'
    }
};

const getInitialName = media => String(media?.displayName || media?.name || '').trim();

const getExtension = name => {
    const lastDot = String(name || '').lastIndexOf('.');
    return lastDot > 0 && lastDot < String(name || '').length - 1
        ? String(name).slice(lastDot)
        : '';
};

const getNameParts = media => {
    const initialName = getInitialName(media);
    const extension = getExtension(initialName) || getExtension(media?.name);
    const baseName = extension && initialName.endsWith(extension)
        ? initialName.slice(0, -extension.length)
        : initialName;

    return { baseName, extension };
};

function RenameFileModal({ isOpen, media, mediaLabel = 'file', onClose, onSubmit }) {
    const {t: lnT} = useTranslation();

    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const { extension } = getNameParts(media);

    useEffect(() => {
        if (isOpen) {
            setName(getNameParts(media).baseName);
            setError('');
            setIsSaving(false);
        }
    }, [isOpen, media]);

    const submit = async event => {
        event.preventDefault();
        const baseName = name.trim();
        const nextName = `${baseName}${extension}`;

        if (!baseName) {
            setError(lnT('common:aNameIsRequired'));
            return;
        }
        if (nextName.length > 255) {
            setError(lnT('common:namesCanBeAtMost255Characters'));
            return;
        }
        if (/[\\/\u0000-\u001F]/.test(nextName)) {
            setError(lnT('common:namesCannotContainPathSeparatorsOrControl'));
            return;
        }

        try {
            setIsSaving(true);
            await onSubmit(nextName);
        } catch (submitError) {
            setError(submitError?.message?.replace(/^GraphQL error:\s*/i, '') || lnT('common:unableToRenameTheFile'));
            setIsSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={isSaving ? undefined : onClose}
            style={modalStyle}
            overlayClassName="Overlay"
            contentLabel={lnT('common:rename', { value1: String(mediaLabel) })}
        >
            <Body>
                <Title>{lnT('common:rename_32a50be4')}{mediaLabel}</Title>
                <Hint>{lnT('common:theExtensionIsFixedSoTheFile')}</Hint>
                <form onSubmit={submit}>
                    <NameRow>
                        <Input
                            autoFocus
                            aria-label={lnT('common:name_b9cecc3a', { value1: String(mediaLabel) })}
                            value={name}
                            maxLength={255 - extension.length}
                            $hasExtension={Boolean(extension)}
                            onChange={event => {
                                setName(event.target.value);
                                setError('');
                            }}
                        />
                        {extension ? <Extension aria-label={lnT('common:fileExtension')}>{extension}</Extension> : null}
                    </NameRow>
                    {error ? <ErrorText role="alert">{error}</ErrorText> : null}
                    <Actions>
                        <SecondaryButton type="button" onClick={onClose} disabled={isSaving}>{lnT('common:cancel')}</SecondaryButton>
                        <PrimaryButton type="submit" disabled={isSaving}>{isSaving ? lnT('common:saving') : lnT('common:saveName')}</PrimaryButton>
                    </Actions>
                </form>
            </Body>
        </Modal>
    );
}

const Body = styled.div`
    padding: 24px;
`;

const Title = styled.div`
    color: ${WHITE};
    font-size: 20px;
    font-weight: 600;
`;

const Hint = styled.p`
    color: ${PALE_WHITE_4};
    font-size: 14px;
    line-height: 1.4;
    margin: 8px 0 16px;
`;

const Input = styled.input`
    width: 100%;
    padding: 13px 14px;
    color: ${WHITE};
    border-radius: ${props => props.$hasExtension ? '12px 0 0 12px' : '12px'};
    border: 1px solid ${PALE_WHITE_1};
    background-color: ${SECONDARY_COLOR};
    font-size: 15px;
    outline: none;

    &:hover { border-color: ${PALE_WHITE_4}; }
    &:focus { border-color: ${PALE_WHITE_6}; }
`;

const NameRow = styled.div`
    display: flex;
    align-items: stretch;
`;

const Extension = styled.div`
    display: flex;
    align-items: center;
    padding: 0 14px;
    color: ${PALE_WHITE_4};
    border: 1px solid ${PALE_WHITE_1};
    border-left: 0;
    border-radius: 0 12px 12px 0;
    background: rgba(255, 255, 255, 0.05);
    font-size: 15px;
    white-space: nowrap;

`;

const ErrorText = styled.div`
    color: #ff8f8f;
    font-size: 13px;
    margin-top: 8px;
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 18px;
`;

const Button = styled.button`
    border-radius: 10px;
    border: 1px solid transparent;
    min-width: 96px;
    height: 40px;
    padding: 0 14px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    &:disabled { cursor: default; opacity: 0.65; }
`;

const SecondaryButton = styled(Button)`
    background: rgba(255, 255, 255, 0.08);
    border-color: ${PALE_WHITE_1};
    color: ${WHITE};

    &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.14); border-color: ${PALE_WHITE_3}; }
`;

const PrimaryButton = styled(Button)`
    background: ${GREEN};
    color: ${WHITE};

    &:hover:not(:disabled) { background: #00a283; }
`;

export default RenameFileModal;
